import { Link } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { summarize } from "../lib/health";
import { usd, relDate, timeAgo } from "../lib/format";
import { Icon } from "../components/icons";
import { HealthRing, Sparkline, SignalDot, HealthBadge, Monogram } from "../components/ui";

export default function Dashboard() {
  const { ws, me } = useWorkspace();
  const s = summarize(ws);

  const openSignals = ws.signals.filter((sig) => sig.status !== "resolved").slice(0, 6);
  const renewals = [...ws.accounts]
    .filter((a) => {
      const d = (new Date(a.renewalDate).getTime() - new Date("2026-08-16").getTime()) / 86400000;
      return d >= 0 && d <= 90;
    })
    .sort((a, b) => +new Date(a.renewalDate) - +new Date(b.renewalDate));
  const myTasks = ws.tasks.filter((t) => t.status !== "done").slice(0, 5);

  const portfolioTrend = ws.accounts[0]?.healthTrend.map((_, i) =>
    Math.round(ws.accounts.reduce((sum, a) => sum + a.healthTrend[i], 0) / ws.accounts.length)
  ) ?? [];

  return (
    <div className="page">
      <h1 className="topbar__title" style={{ display: "none" }}>Command Center</h1>
      <div className="row between mb-24">
        <div>
          <p className="eyebrow">Command Center</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>
            Good morning, {me.name.split(" ")[0]}.
          </h1>
          <p className="muted" style={{ marginTop: 4 }}>
            {s.bands.risk} accounts in the umbra, {s.bands.watch} in the penumbra. {s.renewals90} renewals inside 90 days.
          </p>
        </div>
        <div className="seg-control">
          <button className="on">Portfolio</button>
          <button>My book</button>
        </div>
      </div>

      {/* KPI row */}
      <div className="stats">
        <div className="stat">
          <div className="stat__label"><Icon name="target" size={14} /> Net revenue retention</div>
          <div className="stat__value">{s.nrr}<small>%</small></div>
          <span className={`stat__delta ${s.nrr >= 100 ? "pos" : "neg"}`}>{s.nrr >= 100 ? "▲" : "▼"} {Math.abs(s.nrr - 104)} pts vs target</span>
        </div>
        <div className="stat">
          <div className="stat__label"><Icon name="database" size={14} /> Portfolio ARR</div>
          <div className="stat__value">{usd(s.totalArr)}</div>
          <div className="stat__foot">{ws.accounts.length} accounts · GRR {s.grr}%</div>
        </div>
        <div className="stat">
          <div className="stat__label"><Icon name="pulse" size={14} /> At-risk ARR</div>
          <div className="stat__value" style={{ color: "var(--risk)" }}>{usd(s.atRiskArr)}</div>
          <div className="stat__foot">{s.bands.risk} accounts in the risk band</div>
        </div>
        <div className="stat">
          <div className="stat__label"><Icon name="calendar" size={14} /> Renewing in 90 days</div>
          <div className="stat__value">{usd(s.renewingArr)}</div>
          <div className="stat__foot">{s.renewals90} renewals to steer</div>
        </div>
      </div>

      <div className="grid-main">
        <div style={{ display: "grid", gap: 20 }}>
          {/* Portfolio health */}
          <div className="card">
            <div className="card__hd">
              <div><h3>Portfolio health</h3><span className="sub">Average score across the book, last 12 weeks</span></div>
              <div className="row" style={{ gap: 16 }}>
                <span className="badge badge--good">{s.bands.good} healthy</span>
                <span className="badge badge--watch">{s.bands.watch} watch</span>
                <span className="badge badge--risk">{s.bands.risk} risk</span>
              </div>
            </div>
            <div className="card__bd row" style={{ gap: 28, alignItems: "center" }}>
              <HealthRing value={s.avgHealth} size={92} stroke={7} />
              <div className="grow">
                <Sparkline data={portfolioTrend} width={520} height={72} />
              </div>
            </div>
          </div>

          {/* Signal feed */}
          <div className="card">
            <div className="card__hd">
              <div><h3>Signal feed</h3><span className="sub">Faint changes worth acting on now</span></div>
              <Link to="/signals" className="btn btn--ghost btn--sm">View all <Icon name="arrowRight" size={14} /></Link>
            </div>
            <div className="card__bd" style={{ paddingTop: 6, paddingBottom: 6 }}>
              {openSignals.map((sig) => (
                <div className="feed-item" key={sig.id}>
                  <SignalDot band={sig.band} />
                  <div>
                    <Link to={`/accounts/${sig.accountId}`}><b>{sig.accountName}</b></Link>
                    <small>{sig.title} · <span className="faint">{sig.source}</span></small>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="num" style={{ fontWeight: 600, color: sig.impact < 0 ? "var(--risk)" : "var(--good)", fontSize: 13 }}>
                      {sig.impact > 0 ? "+" : ""}{sig.impact}
                    </div>
                    <time>{timeAgo(sig.at)} ago</time>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* right column */}
        <div style={{ display: "grid", gap: 20 }}>
          <div className="card">
            <div className="card__hd"><h3>Renewals ahead</h3><Link to="/renewals" className="faint" style={{ fontSize: 12 }}>All</Link></div>
            <div className="card__bd" style={{ padding: 8 }}>
              {renewals.slice(0, 5).map((a) => (
                <Link to={`/accounts/${a.id}`} key={a.id} className="feed-item" style={{ gridTemplateColumns: "1fr auto", padding: "11px 8px" }}>
                  <div className="row" style={{ gap: 10 }}>
                    <Monogram name={a.name} />
                    <div>
                      <b style={{ fontSize: 13.5 }}>{a.name}</b>
                      <small>{usd(a.arr)} · {relDate(a.renewalDate)}</small>
                    </div>
                  </div>
                  <HealthBadge band={a.healthBand} />
                </Link>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card__hd"><h3>Your day</h3><span className="tag">{myTasks.length} open</span></div>
            <div className="card__bd" style={{ padding: "6px 8px" }}>
              {myTasks.map((t) => (
                <div className="feed-item" key={t.id} style={{ gridTemplateColumns: "auto 1fr auto", alignItems: "center" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 3, background: t.priority === "high" ? "var(--risk)" : t.priority === "medium" ? "var(--watch)" : "var(--faint)" }} />
                  <div>
                    <b style={{ fontSize: 13 }}>{t.title}</b>
                    <small>{t.accountName} · {t.source === "playbook" ? "from playbook" : "manual"}</small>
                  </div>
                  <time>{relDate(t.due)}</time>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
